import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthProvider";

export interface Capability {
  capability: string;
  app_code: string;
  implemented: boolean;
  allowed: boolean;
  reason: string;
  actions: string[];
}
interface AccessResponse {
  org_id: string;
  evaluated_at: string;
  capabilities: Capability[];
  access_ends_at?: string | null;
  days_remaining?: number | null;
  show_expiry_banner?: boolean;
  grant_source?: string | null;
  access_blocked?: boolean;
  tier?: string | null;
}
type AccessSnapshot = AccessResponse & { userId: string | undefined };
interface AccessState {
  status: "loading" | "ready" | "unavailable";
  capabilities: Capability[];
  can: (capability: string, action?: string) => boolean;
  canApp: (appCode: string) => boolean;
  refresh: () => void;
  daysRemaining: number | null;
  showExpiryBanner: boolean;
  accessEndsAt: string | null;
  accessBlocked: boolean;
  grantSource: string | null;
  isTrial: boolean;
  tier: string | null;
}
const AccessContext = createContext<AccessState | null>(null);

function matchesIdentity(
  snapshot: AccessSnapshot | null,
  orgId: string | null | undefined,
  userId: string | undefined,
): snapshot is AccessSnapshot {
  return Boolean(snapshot && orgId && snapshot.org_id === orgId && snapshot.userId === userId);
}

/** Navigation hints from the same evaluator as API authorization. */
export function AccessProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const orgId = session?.orgId;
  const userId = session?.userId;
  const [response, setResponse] = useState<AccessSnapshot | null>(null);
  const [status, setStatus] = useState<AccessState["status"]>("loading");
  const [revision, setRevision] = useState(0);
  const snapshotRef = useRef<AccessSnapshot | null>(null);
  snapshotRef.current = response;
  const refresh = useCallback(() => {
    setRevision((value) => value + 1);
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    if (!orgId) {
      snapshotRef.current = null;
      setResponse(null);
      setStatus("ready");
      return;
    }
    const keepStale = matchesIdentity(snapshotRef.current, orgId, userId);
    if (!keepStale) {
      snapshotRef.current = null;
      setResponse(null);
      setStatus("loading");
    }
    void api.get<AccessResponse>(`/org/${encodeURIComponent(orgId)}/access`, controller.signal).then((value) => {
      if (controller.signal.aborted) return;
      if (value.org_id !== orgId) { setStatus("unavailable"); return; }
      const next = { ...value, userId };
      snapshotRef.current = next;
      setResponse(next);
      setStatus("ready");
    }).catch(() => {
      if (controller.signal.aborted) return;
      if (matchesIdentity(snapshotRef.current, orgId, userId)) return;
      setStatus("unavailable");
    });
    return () => controller.abort();
  }, [orgId, userId, revision]);
  useEffect(() => {
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refresh]);
  const capabilities = useMemo(() => response && response.org_id === orgId && response.userId === userId ? response.capabilities : [], [response, orgId, userId]);
  const can = useCallback((name: string, action = "read") => status === "ready" && capabilities.some((item) => item.capability === name && item.implemented && item.allowed && item.actions.includes(action)), [capabilities, status]);
  const canApp = useCallback((appCode: string) => status === "ready" && capabilities.some((item) => item.app_code === appCode && item.implemented && item.allowed), [capabilities, status]);
  const daysRemaining = response && response.org_id === orgId && response.userId === userId ? response.days_remaining ?? null : null;
  const showExpiryBanner = Boolean(response && response.org_id === orgId && response.userId === userId && response.show_expiry_banner);
  const accessEndsAt = response && response.org_id === orgId && response.userId === userId ? response.access_ends_at ?? null : null;
  const accessBlocked = Boolean(response && response.org_id === orgId && response.userId === userId && response.access_blocked);
  const grantSource = response && response.org_id === orgId && response.userId === userId ? response.grant_source ?? null : null;
  const isTrial = grantSource === "trial";
  const tier = response && response.org_id === orgId && response.userId === userId ? response.tier ?? null : null;
  const value = useMemo(
    () => ({ status, capabilities, can, canApp, refresh, daysRemaining, showExpiryBanner, accessEndsAt, accessBlocked, grantSource, isTrial, tier }),
    [status, capabilities, can, canApp, refresh, daysRemaining, showExpiryBanner, accessEndsAt, accessBlocked, grantSource, isTrial, tier],
  );
  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess() {
  const value = useContext(AccessContext);
  if (!value) throw new Error("useAccess requires AccessProvider");
  return value;
}
